import { X } from 'lucide-react'

import type { FiltersProps } from '@/components'
import {
  Button,
  FilterChip,
  FilterDialog,
  formatFilterLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components'
import { useDialogActions, useFilters, useFiltersActions } from '@/stores'
import { cn } from '@/utils'

export function Filters({
  dialogId,
  schema,
  getUniqueValues,
  maxVisibleFilters = 3,
  showClearAll = true,
  className,
}: FiltersProps) {
  const filters = useFilters()
  const { setFilters, clearFilters, removeFilter } = useFiltersActions()
  const { openDialog } = useDialogActions()

  const PopContent = PopoverContent as any

  const handleApplyFilter = (filter: any) => {
    const existingIndex = filters.findIndex((f) => f.id === filter.id)

    const newFilters = [...filters]
    if (existingIndex >= 0) {
      newFilters[existingIndex] = filter
    } else {
      newFilters.push(filter)
    }
    setFilters(newFilters)
  }

  const handleEditFilter = (filter: any) => {
    const fieldSchema = schema.find((s) => s.field === filter.field)
    if (fieldSchema) {
      openDialog(dialogId, { schema: fieldSchema, filter })
    }
  }

  const visibleFilters = filters.slice(0, maxVisibleFilters)
  const overflowFilters = filters.slice(maxVisibleFilters)

  return (
    <>
      <div className={cn('flex items-center gap-2 flex-wrap', className)}>
        {visibleFilters.map((filter) => (
          <FilterChip
            key={filter.id}
            label={formatFilterLabel(filter, schema)}
            onClick={() => handleEditFilter(filter)}
            onRemove={() => removeFilter(filter.id)}
          />
        ))}

        {overflowFilters.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button theme="outline" size="sm" className="h-8 gap-1">
                +{overflowFilters.length} more
              </Button>
            </PopoverTrigger>
            <PopContent className="w-auto p-2" align="start">
              <div className="flex flex-col gap-1">
                {overflowFilters.map((filter) => (
                  <FilterChip
                    key={filter.id}
                    label={formatFilterLabel(filter, schema)}
                    onClick={() => handleEditFilter(filter)}
                    onRemove={() => removeFilter(filter.id)}
                  />
                ))}
              </div>
            </PopContent>
          </Popover>
        )}

        {showClearAll && filters.length > 0 && (
          <button
            type="button"
            className="flex items-center gap-1 border border-transparent text-blue-7 text-xs font-medium px-2 py-1 rounded-md hover:bg-blue-2 cursor-pointer"
            onClick={clearFilters}
          >
            <X className="h-2.5 w-2.5" />
            Clear filters
          </button>
        )}
      </div>

      <FilterDialog
        id={dialogId}
        onApply={handleApplyFilter}
        getUniqueValues={getUniqueValues}
      />
    </>
  )
}
